// msal.js
import {
    PublicClientApplication,
    EventType,
    LogLevel,
    InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { getConfig } from "./config";

/* ===================== helpers ===================== */

function asOrigin(val) {
    return val === "origin" ? window.location.origin : val;
}

// Pretty logger that won't explode on circulars
function safeLog(title, obj) {
    // eslint-disable-next-line no-console
    console.log(`\n======== ${title} ========`);
    try {
        const plain = typeof obj === "object" && obj !== null ? { ...obj } : obj;
        // eslint-disable-next-line no-console
        console.log(JSON.stringify(plain, null, 2));
    } catch {
        // eslint-disable-next-line no-console
        console.log(obj);
    }
}

function maskSecret(s, head = 16, tail = 12) {
    if (!s || typeof s !== "string") return s;
    if (s.length <= head + tail + 3) return s.replace(/./g, "*");
    return `${s.slice(0, head)}…${s.slice(-tail)} (len=${s.length})`;
}

function decodeJwtClaims(token) {
    if (!token || typeof token !== "string") return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    try {
        const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const json = decodeURIComponent(
            atob(b64)
                .split("")
                .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join("")
        );
        return JSON.parse(json);
    } catch {
        return null;
    }
}

/* ===================== public API ===================== */

export function getLoginRequest() {
    const a = getConfig().azureEntra || {};
    const base = ["openid", "profile", "email"];
    const extra = Array.isArray(a.scopes) ? a.scopes : [];
    const scopes = Array.from(new Set([...base, ...extra]));
    return { scopes };
}

export function getRedirectStartPage() {
    // Always preserve the exact deep link (path + query), strip hash if present.
    try {
        const href = window.location?.href || "/";
        return href.split("#")[0];
    } catch {
        return "/";
    }
}

/* ===================== msal config ===================== */

function buildMsalConfig(cfg) {
    const a = cfg.azureEntra || {};
    const redirectUri = asOrigin(a.redirectUri);
    const postLogoutRedirectUri = asOrigin(a.postLogoutRedirectUri);

    if (
        cfg.app?.allowedOrigins &&
        !cfg.app.allowedOrigins.includes(window.location.origin)
    ) {
        // eslint-disable-next-line no-console
        console.warn(
            "Current origin not in allowedOrigins. Check public/config.json."
        );
    }

    const debug = !!a.debug;

    return {
        auth: {
            clientId: a.clientId,
            authority: a.authority,
            redirectUri,
            postLogoutRedirectUri,
            // IMPORTANT: turn OFF MSAL's own navigation to eliminate race with our deep-link restore
            navigateToLoginRequestUrl: false,
        },
        cache: {
            cacheLocation: a.cacheLocation || "localStorage",
            storeAuthStateInCookie: !!a.storeAuthStateInCookie,
        },
        system: {
            loggerOptions: {
                logLevel: debug ? LogLevel.Verbose : LogLevel.Info,
                loggerCallback: (level, message, containsPii) => {
                    if (!containsPii) {
                        // eslint-disable-next-line no-console
                        console.log(`[MSAL ${LogLevel[level]}] ${message}`);
                    }
                },
            },
        },
    };
}

/* ===================== singleton + bootstrap ===================== */

let pca = null;
let bootstrapped = false;
let eventsWired = false;

function wireEventsOnce() {
    if (eventsWired || !pca) return;
    eventsWired = true;

    pca.addEventCallback((e) => {
        const a = (getConfig().azureEntra || {});
        const debug = !!a.debug;
        const debugVerbose = !!a.debugVerbose;

        if (
            e.eventType === EventType.LOGIN_SUCCESS ||
            e.eventType === EventType.SSO_SILENT_SUCCESS ||
            e.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ) {
            const account = (e.payload && e.payload.account) || null;
            if (account) {
                pca.setActiveAccount(account);
                // eslint-disable-next-line no-console
                //console.log(
                  //  "[MSAL] Active account set from event:",
                    //account.username
                //);
            }

            if (debug) {
                //safeLog("MSAL Event Type", e.eventType);
                //safeLog("MSAL Event Payload (raw)", e.payload);

                if (e.payload?.idTokenClaims) {
                  //  safeLog("ID Token Claims", e.payload.idTokenClaims);
                }

                if (e.payload?.accessToken) {
                    /*safeLog("Access Token (masked)", {
                        masked: maskSecret(e.payload.accessToken),
                        tokenType: e.payload.tokenType,
                        scopes: e.payload.scopes,
                        expiresOn: e.payload.expiresOn,
                        fromCache: e.payload.fromCache,
                        extExpiresOn: e.payload.extExpiresOn,
                    }); */

                    const atClaims = decodeJwtClaims(e.payload.accessToken);
                    if (atClaims) {
                        //safeLog("Access Token Claims (decoded)", atClaims);
                    } else if (debugVerbose) {
                        // eslint-disable-next-line no-console
                        console.log(
                            "[MSAL] Access token is not a JWT or could not be decoded."
                        );
                    }

                    if (debugVerbose) {
                        // eslint-disable-next-line no-console
                       // console.log("Access Token (FULL, sensitive):", e.payload.accessToken);
                    }
                }

                if (account) {
                    /*safeLog("Account Object", {
                        username: account.username,
                        homeAccountId: account.homeAccountId,
                        environment: account.environment,
                        tenantId: account.tenantId,
                        localAccountId: account.localAccountId,
                        name: account.name,
                        idTokenClaims:
                            a.debugVerbose && account.idTokenClaims ? account.idTokenClaims : undefined,
                    }); */
                }
            }
        }

        if (
            e.eventType === EventType.LOGIN_FAILURE ||
            e.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
            e.eventType === EventType.SSO_SILENT_FAILURE ||
            e.eventType === EventType.HANDLE_REDIRECT_END
        ) {
            const hashHasError =
                typeof window !== "undefined" &&
                window.location?.hash?.includes("error=");
            if (hashHasError) {
                const params = new URLSearchParams(
                    window.location.hash.replace(/^#/, "")
                );
                const code = params.get("error");
                const desc = decodeURIComponent(
                    params.get("error_description") || ""
                );
                // eslint-disable-next-line no-console
                console.error("[MSAL] Redirect error", { code, desc });
            }

            if ((getConfig().azureEntra || {}).debug) {
                //safeLog("MSAL Event (error-ish) Type", e.eventType);
                if (e.error) {
                  /*  safeLog("MSAL Event Error", {
                        name: e.error.name,
                        message: e.error.message,
                        errorCode: e.error.errorCode,
                        subError: e.error.subError,
                        stack: (getConfig().azureEntra || {}).debugVerbose ? e.error.stack : undefined,
                    });*/
                }
            }
        }
    });
}

export async function initMsal() {
    if (!pca) {
        const cfg = getConfig();
        pca = new PublicClientApplication(buildMsalConfig(cfg));
        await pca.initialize();
        wireEventsOnce();
    }
    if (bootstrapped) return pca;

    // eslint-disable-next-line no-console
    console.log("[MSAL] initMsal(): handling redirect…");
    try {
        const resp = await pca.handleRedirectPromise();
        if (!resp && typeof window !== "undefined" && window.location.hash.includes("error=")) {
            const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
            const code = params.get("error");
            const desc = decodeURIComponent(params.get("error_description") || "");
            // eslint-disable-next-line no-console
            console.error("[MSAL] Redirect error", { code, desc });
        }

        if (resp?.account) {
            pca.setActiveAccount(resp.account);
            // eslint-disable-next-line no-console
            console.log("[MSAL] redirect handled; account:", resp.account.username);
        } else {
            const cached = pca.getActiveAccount() || pca.getAllAccounts()[0];
            if (cached) {
                pca.setActiveAccount(cached);
                // eslint-disable-next-line no-console
                //console.log("[MSAL] using cached account:", cached.username);
            } else {
                // eslint-disable-next-line no-console
                console.log("[MSAL] no account in cache");
            }
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[MSAL] handleRedirectPromise error", err);
    }

    bootstrapped = true;
    return pca;
}

export function getMsalInstance() {
    if (!pca) throw new Error("MSAL not initialized. Call initMsal() first.");
    return pca;
}

export async function getToken(options = {}) {
    const { forceRefresh = false } = options || {};

    if (!pca) await initMsal();
    const account = pca.getActiveAccount() || pca.getAllAccounts()[0];
    if (!account) {
        // eslint-disable-next-line no-console
        console.warn("[MSAL] getToken(): no account available");
        return "";
    }

    const a = (getConfig().azureEntra || {});
    const debug = !!a.debug;
    const debugVerbose = !!a.debugVerbose;

    try {
        const req = { ...getLoginRequest(), account, forceRefresh: !!forceRefresh };
        //if (debug) safeLog("acquireTokenSilent.request", req);

        const res = await pca.acquireTokenSilent(req);

        if (debug) {
           /* safeLog("Token Result (metadata)", {
                scopes: res.scopes,
                fromCache: res.fromCache,
                tokenType: res.tokenType,
                tenantId: res.tenantId,
                idToken: a.debugVerbose ? res.idToken : `(masked) ${maskSecret(res.idToken)}`,
                expiresOn: res.expiresOn,
                extExpiresOn: res.extExpiresOn,
                account: {
                    username: res.account?.username,
                    homeAccountId: res.account?.homeAccountId,
                    environment: res.account?.environment,
                    tenantId: res.account?.tenantId,
                    localAccountId: res.account?.localAccountId,
                },
            });*/

            //if (res.idTokenClaims) safeLog("ID Token Claims", res.idTokenClaims);

            //safeLog("Access Token (masked)", { masked: maskSecret(res.accessToken) });
            if (debugVerbose) {
                // eslint-disable-next-line no-console
               // console.log("Access Token (FULL, sensitive):", res.accessToken);
            }

            const atClaims = decodeJwtClaims(res.accessToken);
            //if (atClaims) safeLog("Access Token Claims (decoded)", atClaims);
        }

        return res.accessToken;
    } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
            // eslint-disable-next-line no-console
            console.log("[MSAL] token needs interaction → acquireTokenRedirect");
            await pca.acquireTokenRedirect({
                ...getLoginRequest(),
                account,
                redirectStartPage: typeof window !== "undefined" ? window.location.href : undefined,
            });
            return "";
        }

        const err = /** @type {any} */ (e);
        const enriched = {
            name: err?.name,
            message: err?.message,
            errorCode: err?.errorCode,
            subError: err?.subError,
            correlationId: err?.correlationId,
            stack: a.debugVerbose ? err?.stack : undefined,
        };
        // eslint-disable-next-line no-console
        console.error("[MSAL] acquireTokenSilent error", enriched);
        throw e;
    }
}

export async function logout() {
    if (!pca) await initMsal();
    const account = pca.getActiveAccount() || pca.getAllAccounts()[0];
    await pca.logoutRedirect({ account });
}


/**
 * getTokenInfo()
 * Returns access token + expiry in ms (best effort).
 * This is useful for downstream cookie-session refresh logic.
 */
export async function getTokenInfo(options = {}) {
    const token = await getToken(options);
    if (!token) return { token: "", expMs: 0 };
    const claims = decodeJwtClaims(token) || {};
    const expMs = claims && claims.exp ? Number(claims.exp) * 1000 : 0;
    return { token, expMs };
}
